import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { couponService } from '@/services/couponService'
import type { Coupon, CreateCouponInput, UpdateCouponInput } from '@/types/coupon'
import { formatDiscount } from '@/types/coupon'

type StatusFilter = 'all' | 'active' | 'inactive'

interface CouponFormData {
  code: string
  name: string
  description: string
  discount_type: 'percentage' | 'fixed'
  discount_value: number
  max_uses: string
  max_uses_per_user: number
  minimum_purchase: string
  valid_from: string
  valid_until: string
}

const initialFormData: CouponFormData = {
  code: '',
  name: '',
  description: '',
  discount_type: 'percentage',
  discount_value: 0,
  max_uses: '',
  max_uses_per_user: 1,
  minimum_purchase: '',
  valid_from: '',
  valid_until: '',
}

export const AdminCouponsPage: React.FC = () => {
  const navigate = useNavigate()
  const [coupons, setCoupons] = useState<Coupon[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null)
  const [formData, setFormData] = useState<CouponFormData>(initialFormData)
  const [formLoading, setFormLoading] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletingCoupon, setDeletingCoupon] = useState<Coupon | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const fetchCoupons = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params: { isActive?: boolean; search?: string; limit?: number } = { limit: 50 }
      if (statusFilter === 'active') params.isActive = true
      else if (statusFilter === 'inactive') params.isActive = false
      if (search) params.search = search
      const data = await couponService.listCoupons(params)
      setCoupons(data)
    } catch (e) {
      setError((e as Error).message || 'Gagal memuat kupon')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, search])

  useEffect(() => {
    void fetchCoupons()
  }, [fetchCoupons])

  const handleSearch = () => {
    setSearch(searchInput)
  }

  const totalCoupons = coupons.length
  const activeCoupons = coupons.filter((c) => c.isActive).length
  const totalUsages = coupons.reduce((acc, c) => acc + c.currentUses, 0)

  const openCreateModal = () => {
    setEditingCoupon(null)
    setFormData({ ...initialFormData, valid_from: new Date().toISOString().slice(0, 16) })
    setFormError(null)
    setShowModal(true)
  }

  const openEditModal = (coupon: Coupon) => {
    setEditingCoupon(coupon)
    setFormData({
      code: coupon.code,
      name: coupon.name,
      description: coupon.description || '',
      discount_type: coupon.discountType,
      discount_value: coupon.discountValue,
      max_uses: coupon.maxUses != null ? String(coupon.maxUses) : '',
      max_uses_per_user: coupon.maxUsesPerUser,
      minimum_purchase: coupon.minimumPurchase > 0 ? String(coupon.minimumPurchase) : '',
      valid_from: coupon.validFrom ? new Date(coupon.validFrom).toISOString().slice(0, 16) : '',
      valid_until: coupon.validUntil ? new Date(coupon.validUntil).toISOString().slice(0, 16) : '',
    })
    setFormError(null)
    setShowModal(true)
  }

  const handleFormChange = (field: keyof CouponFormData, value: string | number) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value }
      if (field === 'code') next.code = String(value).toUpperCase()
      return next
    })
  }

  const handleSubmit = async () => {
    setFormError(null)
    if (!formData.code || !formData.name || formData.discount_value <= 0) {
      setFormError('Mohon lengkapi semua kolom yang wajib diisi')
      return
    }

    setFormLoading(true)
    try {
      if (editingCoupon) {
        const input: UpdateCouponInput = {
          name: formData.name,
          description: formData.description || undefined,
          discount_type: formData.discount_type,
          discount_value: formData.discount_value,
          max_uses: formData.max_uses ? Number(formData.max_uses) : undefined,
          max_uses_per_user: formData.max_uses_per_user,
          minimum_purchase: formData.minimum_purchase ? Number(formData.minimum_purchase) : undefined,
          valid_from: formData.valid_from ? new Date(formData.valid_from).toISOString() : undefined,
          valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : undefined,
        }
        await couponService.updateCoupon(editingCoupon.id, input)
      } else {
        const input: CreateCouponInput = {
          code: formData.code,
          name: formData.name,
          description: formData.description || undefined,
          discount_type: formData.discount_type,
          discount_value: formData.discount_value,
          max_uses: formData.max_uses ? Number(formData.max_uses) : undefined,
          max_uses_per_user: formData.max_uses_per_user,
          minimum_purchase: formData.minimum_purchase ? Number(formData.minimum_purchase) : undefined,
          valid_from: formData.valid_from ? new Date(formData.valid_from).toISOString() : undefined,
          valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : undefined,
        }
        await couponService.createCoupon(input)
      }
      setShowModal(false)
      await fetchCoupons()
    } catch (e) {
      setFormError((e as Error).message || 'Terjadi kesalahan')
    } finally {
      setFormLoading(false)
    }
  }

  const openDeleteModal = (coupon: Coupon) => {
    setDeletingCoupon(coupon)
    setShowDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!deletingCoupon) return
    setDeleteLoading(true)
    try {
      await couponService.deleteCoupon(deletingCoupon.id)
      setShowDeleteModal(false)
      setDeletingCoupon(null)
      await fetchCoupons()
    } catch (e) {
      setError((e as Error).message || 'Gagal menghapus kupon')
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleToggleActive = async (coupon: Coupon) => {
    try {
      if (coupon.isActive) {
        await couponService.deactivateCoupon(coupon.id)
      } else {
        await couponService.activateCoupon(coupon.id)
      }
      await fetchCoupons()
    } catch (e) {
      setError((e as Error).message || 'Gagal mengubah status kupon')
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Tanpa batas'
    return new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  return (
    <div>
      <PageHeader
        title="Manajemen Kupon"
        subtitle={`${totalCoupons} kupon terdaftar`}
        actions={
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">add</span>
            Buat Kupon Baru
          </button>
        }
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="material-symbols-outlined text-primary text-[20px]">local_offer</span>
            <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Total Kupon</p>
          </div>
          <p className="text-gray-900 dark:text-white text-2xl font-bold">{totalCoupons}</p>
        </div>
        <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="material-symbols-outlined text-green-400 text-[20px]">check_circle</span>
            <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Aktif</p>
          </div>
          <p className="text-gray-900 dark:text-white text-2xl font-bold">{activeCoupons}</p>
        </div>
        <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="material-symbols-outlined text-indigo-400 text-[20px]">group</span>
            <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Total Penggunaan</p>
          </div>
          <p className="text-gray-900 dark:text-white text-2xl font-bold">{totalUsages}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
          {(['all', 'active', 'inactive'] as StatusFilter[]).map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg text-xs font-bold uppercase transition-colors whitespace-nowrap ${statusFilter === status
                ? 'bg-primary text-white'
                : 'bg-white dark:bg-surface-card text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-white/5'
                }`}
            >
              {status === 'all' ? 'Semua' : status === 'active' ? 'Aktif' : 'Nonaktif'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Cari kupon..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="flex-1 sm:w-64 px-4 py-2 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50"
          />
          <button
            onClick={handleSearch}
            className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">search</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="border-b border-gray-200 dark:border-white/5">
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Kode</th>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Nama</th>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Diskon</th>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Penggunaan</th>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Status</th>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Berlaku Hingga</th>
                <th className="text-right px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-white/5">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-gray-500 dark:text-text-muted">Loading...</td>
                </tr>
              ) : coupons.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-gray-500 dark:text-text-muted">Tidak ada kupon ditemukan</td>
                </tr>
              ) : (
                coupons.map((coupon) => (
                  <tr key={coupon.id} className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
                    <td className="px-5 py-4">
                      <button
                        onClick={() => copyToClipboard(coupon.code)}
                        className="font-mono text-sm font-bold text-primary hover:underline"
                        title="Klik untuk menyalin"
                      >
                        {coupon.code}
                      </button>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white">{coupon.name}</td>
                    <td className="px-5 py-4">
                      <span className="inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold bg-primary/10 text-primary">
                        {formatDiscount(coupon)}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {coupon.currentUses} / {coupon.maxUses != null ? coupon.maxUses : '∞'}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${coupon.isActive ? 'bg-emerald-500 text-white' : 'bg-slate-400 text-white'}`}>
                        {coupon.isActive ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-500 dark:text-text-muted">
                      {formatDate(coupon.validUntil)}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => navigate(`/admin/coupons/${coupon.id}`)}
                          className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                          title="Lihat Detail"
                        >
                          <span className="material-symbols-outlined text-[18px]">visibility</span>
                        </button>
                        <button
                          onClick={() => openEditModal(coupon)}
                          className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                          title="Edit"
                        >
                          <span className="material-symbols-outlined text-[18px]">edit</span>
                        </button>
                        <button
                          onClick={() => handleToggleActive(coupon)}
                          className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                          title={coupon.isActive ? 'Nonaktifkan' : 'Aktifkan'}
                        >
                          <span className={`material-symbols-outlined text-[18px] ${coupon.isActive ? 'text-yellow-500' : 'text-green-500'}`}>
                            {coupon.isActive ? 'pause' : 'play_arrow'}
                          </span>
                        </button>
                        <button
                          onClick={() => openDeleteModal(coupon)}
                          className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-500/10 text-gray-500 dark:text-gray-400 hover:text-red-500 transition-colors"
                          title="Hapus"
                        >
                          <span className="material-symbols-outlined text-[18px]">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Error Toast */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-500/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm z-50">
          {error}
          <button onClick={() => setError(null)} className="ml-3 text-red-300 hover:text-white">Dismiss</button>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-gray-50 dark:bg-surface-dark rounded-lg w-full max-w-lg border border-gray-200 dark:border-white/10 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-white/10">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {editingCoupon ? 'Edit Kupon' : 'Buat Kupon Baru'}
              </h2>
              <button onClick={() => setShowModal(false)} className="size-8 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 flex items-center justify-center transition-colors">
                <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">close</span>
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Code */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-1.5">Kode Kupon *</label>
                <input
                  type="text"
                  maxLength={20}
                  value={formData.code}
                  onChange={(e) => handleFormChange('code', e.target.value)}
                  disabled={!!editingCoupon}
                  placeholder="Contoh: DISKON20"
                  className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50 disabled:opacity-50 font-mono"
                />
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">3-20 karakter, otomatis huruf besar</p>
              </div>

              {/* Name */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-1.5">Nama *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  placeholder="Contoh: Diskon Awal Tahun"
                  className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-1.5">Deskripsi</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  rows={2}
                  placeholder="Deskripsi kupon (opsional)"
                  className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50 resize-none"
                />
              </div>

              {/* Discount Type */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-1.5">Tipe Diskon *</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="discount_type"
                      value="percentage"
                      checked={formData.discount_type === 'percentage'}
                      onChange={() => handleFormChange('discount_type', 'percentage')}
                      className="text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Persentase</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="discount_type"
                      value="fixed"
                      checked={formData.discount_type === 'fixed'}
                      onChange={() => handleFormChange('discount_type', 'fixed')}
                      className="text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Nominal Tetap</span>
                  </label>
                </div>
              </div>

              {/* Discount Value */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-1.5">
                  Nilai Diskon * {formData.discount_type === 'percentage' ? '(%)' : '(Rp)'}
                </label>
                <input
                  type="number"
                  min={1}
                  max={formData.discount_type === 'percentage' ? 100 : undefined}
                  value={formData.discount_value || ''}
                  onChange={(e) => handleFormChange('discount_value', Number(e.target.value))}
                  className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50"
                />
              </div>

              {/* Max Uses */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-1.5">Maks. Penggunaan</label>
                  <input
                    type="number"
                    min={1}
                    value={formData.max_uses}
                    onChange={(e) => handleFormChange('max_uses', e.target.value)}
                    placeholder="Tak terbatas"
                    className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-1.5">Maks. Per User</label>
                  <input
                    type="number"
                    min={1}
                    value={formData.max_uses_per_user}
                    onChange={(e) => handleFormChange('max_uses_per_user', Number(e.target.value))}
                    className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50"
                  />
                </div>
              </div>

              {/* Minimum Purchase */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-1.5">Pembelian Minimum (Rp)</label>
                <input
                  type="number"
                  min={0}
                  value={formData.minimum_purchase}
                  onChange={(e) => handleFormChange('minimum_purchase', e.target.value)}
                  placeholder="Tak ada minimum"
                  className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50"
                />
              </div>

              {/* Valid From */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-1.5">Berlaku Dari</label>
                <input
                  type="datetime-local"
                  value={formData.valid_from}
                  onChange={(e) => handleFormChange('valid_from', e.target.value)}
                  className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50"
                />
              </div>

              {/* Valid Until */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-1.5">Berlaku Hingga</label>
                <input
                  type="datetime-local"
                  value={formData.valid_until}
                  onChange={(e) => handleFormChange('valid_until', e.target.value)}
                  className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50"
                />
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Kosongkan jika tidak ada batas waktu</p>
              </div>

              {formError && (
                <p className="text-sm text-red-400">{formError}</p>
              )}
            </div>

            <div className="flex gap-3 p-5 border-t border-gray-200 dark:border-white/10">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2.5 rounded-lg bg-gray-100 dark:bg-white/5 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
              >
                Batal
              </button>
              <button
                onClick={handleSubmit}
                disabled={formLoading}
                className="flex-1 py-2.5 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formLoading ? 'Menyimpan...' : editingCoupon ? 'Simpan Perubahan' : 'Buat Kupon'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingCoupon && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-gray-50 dark:bg-surface-dark rounded-lg p-6 w-full max-w-md border border-gray-200 dark:border-white/10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="size-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <span className="material-symbols-outlined text-red-400">warning</span>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Hapus Kupon</h2>
                <p className="text-sm text-gray-500 dark:text-text-muted">Tindakan ini tidak dapat dibatalkan</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-6">
              Anda yakin ingin menghapus kupon <span className="font-mono font-bold">{deletingCoupon.code}</span>?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 py-2.5 rounded-lg bg-gray-100 dark:bg-white/5 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
              >
                Batal
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteLoading}
                className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteLoading ? 'Menghapus...' : 'Hapus'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminCouponsPage
